'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { useApiFetch } from '@/features/auth'
import {
  fetchInvestorCases,
  fetchInvestorDetail,
  fetchInvestorReveal,
  updateInvestorDetail,
  type UpdateInvestorInput,
} from '@/features/investors/api/investors.api'
import { AppError } from '@/lib/errors/appError'
import { queryKeys } from '@/lib/queryKeys'

function toastError(error: unknown) {
  toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
}

export function useInvestorDetail(investorId: string | undefined) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.investorsResource.detail(investorId ?? ''),
    queryFn: () => fetchInvestorDetail(authedFetch, investorId!),
    enabled: !!investorId,
  })
}

/** The "Edit investor" dialog's real-value fetch — `enabled` gates it to only run while the
 * dialog is actually open, same lazy pattern as `use-call-recording-audio.ts`'s recording fetch,
 * so it never rides along on normal page load. */
export function useInvestorReveal(investorId: string | undefined, options: { enabled?: boolean }) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.investorsResource.reveal(investorId ?? ''),
    queryFn: () => fetchInvestorReveal(authedFetch, investorId!),
    enabled: !!investorId && (options.enabled ?? true),
  })
}

export function useInvestorCasesForStaff(investorId: string | undefined) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.investorsResource.cases(investorId ?? ''),
    queryFn: () => fetchInvestorCases(authedFetch, investorId!),
    enabled: !!investorId,
  })
}

export function useUpdateInvestorDetail(investorId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateInvestorInput) =>
      updateInvestorDetail(authedFetch, investorId, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.investorsResource.detail(investorId), updated)
      void queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'investors-resource' && q.queryKey[1] === 'list',
      })
      // A case's `CaseDetailDto.investor` embeds this same investor's name/email/mobile/pan/city
      // — this mutation has no `caseId` to invalidate a single case-detail query directly (one
      // investor can have several cases), so this predicate-invalidates every open one, same
      // "the caller doesn't know all affected keys" reasoning as the calls-list invalidation in
      // `use-case-subresource-writes.ts`. Without this, `client-detail.tsx`'s header (email/
      // mobile) kept showing stale values after a save here.
      void queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'cases' && q.queryKey[1] === 'real',
      })
      toast({ variant: 'success', title: 'Investor details saved' })
    },
    onError: toastError,
  })
}
